import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format, startOfWeek, endOfWeek, addDays, addWeeks, subWeeks, isSameDay, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  User,
  Phone,
  MapPin,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  CalendarDays,
  Users,
  Filter,
  Euro,
  FileText,
  Building,
  CreditCard,
  Eye,
  EyeOff,
  CalendarPlus
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { getUserRole, isTelepro } from "./Login";

const API = `${typeof window !== "undefined" ? window.location.origin : ""}/api`;

const RDV_STATUS_CONFIG = {
  planifie: { label: "Planifié", color: "bg-blue-500", textColor: "text-blue-700", bgLight: "bg-blue-500/10", border: "border-blue-500/30" },
  realise: { label: "Réalisé", color: "bg-emerald-500", textColor: "text-emerald-700", bgLight: "bg-emerald-500/10", border: "border-emerald-500/30" },
  annule: { label: "Annulé", color: "bg-red-500", textColor: "text-red-700", bgLight: "bg-red-500/10", border: "border-red-500/30" },
};

// Color coding by rdv_type
const RDV_TYPE_COLORS = {
  rdv_admin: { dot: "bg-blue-500", bg: "bg-blue-500/10", border: "border-blue-500/30", label: "RDV Admin" },
  devis: { dot: "bg-amber-500", bg: "bg-amber-500/10", border: "border-amber-500/30", label: "Devis" },
  travaux: { dot: "bg-violet-500", bg: "bg-violet-500/10", border: "border-violet-500/30", label: "Travaux" },
  encaissement: { dot: "bg-emerald-500", bg: "bg-emerald-500/10", border: "border-emerald-500/30", label: "Encaissement" },
  etape: { dot: "bg-muted/300", bg: "bg-muted/30", border: "border-gray-200", label: "Étape" },
};

const HOURS = Array.from({ length: 24 }, (_, i) => {
  const h = Math.floor(i / 2) + 8;
  const m = i % 2 === 0 ? "00" : "30";
  return `${h.toString().padStart(2, '0')}:${m}`;
}); // 8h00 à 19h30 par demi-heure

// Personnes autorisées pour les télépros (leur propre planning + Michael)
const TELEPRO_ALLOWED_PERSONS = ["Martine", "Eddy", "Dylan", "Michael"];

export default function PlanningPage() {
  const navigate = useNavigate();
  const [rdvs, setRdvs] = useState([]);
  const [paiementsApf, setPaiementsApf] = useState([]);
  const [loading, setLoading] = useState(true);
  const [persons, setPersons] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState("all");
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [selectedRdv, setSelectedRdv] = useState(null);
  const [showNewRdvModal, setShowNewRdvModal] = useState(false);
  const [newRdvSlot, setNewRdvSlot] = useState(null);
  const [newRdvForm, setNewRdvForm] = useState({ client_nom: "", client_telephone: "", programme: "", notes: "", dossier_id: "" });
  const [savingNewRdv, setSavingNewRdv] = useState(false);
  const [dossiersList, setDossiersList] = useState([]);
  const [selectedPaiement, setSelectedPaiement] = useState(null);
  const [showRdvDialog, setShowRdvDialog] = useState(false);
  const [showPaiementDialog, setShowPaiementDialog] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [showPaiementsApf, setShowPaiementsApf] = useState(true); // Toggle pour afficher/masquer les paiements
  
  // Vérifier les permissions
  const userRole = getUserRole();
  const userName = localStorage.getItem("label_energie_user") || "";
  const isUserTelepro = isTelepro();
  const isRegie = userRole === "regie" || userRole === "telepro_dynamic";
  const isNonAdmin = isUserTelepro || isRegie;
  const canViewPaiements = userRole === "admin_full" || userRole === "admin_restricted";
  
  // Check if current user should see RDV details or just "Occupé"
  const shouldShowOccupe = (rdv) => {
    if (!isNonAdmin) return false; // Admin sees everything
    // Show details if RDV was created by this user or assigned to them
    if (rdv.created_by === userName || rdv.attribue_a === userName) return false;
    // Otherwise show "Occupé" for admin users' slots
    return true;
  };
  
  // Filtrer les personnes visibles pour les télépros
  const getVisiblePersons = (allPersons) => {
    if (isUserTelepro || isRegie) {
      // Télépro/Régie voit son propre planning + tous les users (mais "Occupé" pour les autres)
      return allPersons;
    }
    return allPersons;
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    fetchPersons();
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    fetchRdvs();
    if (canViewPaiements) {
      fetchPaiementsApf();
    }
  }, [currentWeekStart, selectedPerson]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const fetchPersons = async () => {
    try {
      const response = await axios.get(`${API}/planning/persons`);
      const allPersons = response.data.persons;
      // Filtrer les personnes visibles pour les télépros
      setPersons(getVisiblePersons(allPersons));
    } catch (error) {
      console.error("Error fetching persons:", error);
    }
  };

  const fetchPaiementsApf = async () => {
    try {
      const weekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 1 });
      const params = new URLSearchParams({
        date_debut: format(currentWeekStart, "yyyy-MM-dd"),
        date_fin: format(weekEnd, "yyyy-MM-dd")
      });
      
      const response = await axios.get(`${API}/planning/paiements-apf?${params}`);
      setPaiementsApf(response.data.paiements);
    } catch (error) {
      console.error("Error fetching paiements APF:", error);
    }
  };

  const fetchRdvs = async () => {
    setLoading(true);
    try {
      const weekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 1 });
      const params = new URLSearchParams({
        date_debut: format(currentWeekStart, "yyyy-MM-dd"),
        date_fin: format(weekEnd, "yyyy-MM-dd")
      });
      
      if (selectedPerson !== "all") {
        params.append("personne", selectedPerson);
      }

      const response = await axios.get(`${API}/planning/rdvs?${params}`);
      setRdvs(response.data.rdvs);
    } catch (error) {
      console.error("Error fetching RDVs:", error);
      toast.error("Erreur lors du chargement des RDV");
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousWeek = () => {
    setCurrentWeekStart(subWeeks(currentWeekStart, 1));
  };

  const handleNextWeek = () => {
    setCurrentWeekStart(addWeeks(currentWeekStart, 1));
  };

  const handleToday = () => {
    setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));
  };

  const handleRdvClick = (rdv) => {
    setSelectedRdv(rdv);
    setShowRdvDialog(true);
  };

  const handlePaiementClick = (paiement) => {
    setSelectedPaiement(paiement);
    setShowPaiementDialog(true);
  };

  // Create RDV from empty slot
  const handleCreateNewRdv = async () => {
    if (!newRdvSlot || !newRdvForm.client_nom.trim()) return;
    setSavingNewRdv(true);
    try {
      await axios.post(`${API}/planning/rdvs`, {
        date: newRdvSlot.date,
        heure: newRdvSlot.heure,
        attribue_a: newRdvSlot.personne || "",
        client_nom: newRdvForm.client_nom,
        client_telephone: newRdvForm.client_telephone,
        programme: newRdvForm.programme,
        notes: newRdvForm.notes,
        created_by: localStorage.getItem("label_energie_user") || "",
        rdv_type: "rdv_admin",
        dossier_id: newRdvForm.dossier_id || null,
      });
      toast.success("RDV créé");
      setShowNewRdvModal(false);
      setNewRdvForm({ client_nom: "", client_telephone: "", programme: "", notes: "", dossier_id: "" });
      fetchRdvs();
    } catch (err) { toast.error("Erreur"); }
    setSavingNewRdv(false);
  };

  // Fetch dossiers for linking
  useEffect(() => {
    axios.get(`${API}/dossiers`).then(res => setDossiersList(res.data || [])).catch(() => {});
  }, []);


  // Format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "-";
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const handleUpdateRdvStatus = async (newStatus) => {
    if (!selectedRdv) return;
    
    setUpdating(true);
    try {
      await axios.put(`${API}/planning/rdvs/${selectedRdv.id}`, { statut: newStatus });
      toast.success("Statut du RDV mis à jour");
      setShowRdvDialog(false);
      fetchRdvs();
    } catch (error) {
      console.error("Error updating RDV:", error);
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteRdv = async () => {
    if (!selectedRdv || !confirm("Êtes-vous sûr de vouloir supprimer ce RDV ?")) return;
    
    setUpdating(true);
    try {
      await axios.delete(`${API}/planning/rdvs/${selectedRdv.id}`);
      toast.success("RDV supprimé");
      setShowRdvDialog(false);
      fetchRdvs();
    } catch (error) {
      console.error("Error deleting RDV:", error);
      toast.error("Erreur lors de la suppression");
    } finally {
      setUpdating(false);
    }
  };

  // Get days of the current week
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));

  // Get RDVs for a specific day and hour
  const getRdvsForSlot = (day, hourSlot) => {
    return rdvs.filter(rdv => {
      const rdvDate = parseISO(rdv.date);
      // Match RDV hour to slot: "09:30" matches "09:30", "09:00" matches "09:00"
      const rdvTime = rdv.heure?.substring(0, 5) || "";
      return isSameDay(rdvDate, day) && rdvTime === hourSlot;
    });
  };

  // Get paiements APF for a specific day (affichés en haut de journée, heure 8h)
  const getPaiementsForDay = (day) => {
    if (!canViewPaiements || !showPaiementsApf) return [];
    return paiementsApf.filter(paiement => {
      const paiementDate = parseISO(paiement.date);
      return isSameDay(paiementDate, day);
    });
  };

  // Count RDVs by person
  const rdvCountByPerson = persons.reduce((acc, person) => {
    acc[person] = rdvs.filter(r => r.attribue_a === person).length;
    return acc;
  }, {});

  if (loading && rdvs.length === 0) {
    return (
      <div className="p-6 md:p-12 animate-pulse">
        <div className="h-8 bg-muted rounded w-1/3 mb-8"></div>
        <div className="h-96 bg-muted rounded-xl"></div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-12 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight mb-2">
            Planning
          </h1>
          <p className="text-muted-foreground">
            {isUserTelepro 
              ? `Votre planning et celui de Michael` 
              : "Gestion des rendez-vous par personne"
            }
          </p>
          {/* Legend */}
          <div className="flex gap-3 mt-2 flex-wrap">
            {Object.entries(RDV_TYPE_COLORS).map(([key, cfg]) => (
              <div key={key} className="flex items-center gap-1.5 text-xs">
                <div className={cn("w-2.5 h-2.5 rounded-full", cfg.dot)} />
                <span className="text-muted-foreground">{cfg.label}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Toggle Paiements APF - visible uniquement pour Admin/Renova */}
        <div className="flex items-center gap-3">
          <Button onClick={() => { setNewRdvSlot({ date: format(new Date(), "yyyy-MM-dd"), heure: "09:00", personne: selectedPerson }); setShowNewRdvModal(true); }}
            className="rounded-xl bg-blue-500 hover:bg-blue-600 text-white" data-testid="btn-create-rdv">
            <CalendarPlus className="w-4 h-4 mr-2" />Créer un RDV
          </Button>
          {canViewPaiements && (
          <div className="flex items-center gap-3 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-xl">
            <Euro className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-800">Paiements APF</span>
            <Switch
              checked={showPaiementsApf}
              onCheckedChange={setShowPaiementsApf}
              className="data-[state=checked]:bg-amber-500"
              data-testid="toggle-paiements-apf"
            />
            {showPaiementsApf ? (
              <Eye className="w-4 h-4 text-amber-600" />
            ) : (
              <EyeOff className="w-4 h-4 text-muted-foreground" />
            )}
          </div>
          )}
        </div>
      </div>

      {/* Person Stats */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Button
          variant={selectedPerson === "all" ? "default" : "outline"}
          size="sm"
          onClick={() => setSelectedPerson("all")}
          className={cn(
            "rounded-xl",
            selectedPerson === "all" && "bg-[hsl(var(--renova-green))] hover:bg-[hsl(var(--renova-green))]/90"
          )}
        >
          <Users className="w-4 h-4 mr-2" />
          Tous ({rdvs.length})
        </Button>
        {persons.map((person) => (
          <Button
            key={person}
            variant={selectedPerson === person ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedPerson(person)}
            className={cn(
              "rounded-xl",
              selectedPerson === person && "bg-[hsl(var(--renova-green))] hover:bg-[hsl(var(--renova-green))]/90"
            )}
          >
            <User className="w-4 h-4 mr-2" />
            {person} ({rdvCountByPerson[person] || 0})
          </Button>
        ))}
      </div>

      {/* Week Navigation */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handlePreviousWeek} className="rounded-xl">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={handleToday} className="rounded-xl">
            Aujourd'hui
          </Button>
          <Button variant="outline" size="sm" onClick={handleNextWeek} className="rounded-xl">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <h2 className="font-semibold text-lg">
          {format(currentWeekStart, "d MMMM", { locale: fr })} - {format(addDays(currentWeekStart, 6), "d MMMM yyyy", { locale: fr })}
        </h2>
      </div>

      {/* Calendar Grid */}
      <div className="border border-border rounded-xl bg-card overflow-hidden">
        {/* Header Row - Days */}
        <div className="grid grid-cols-8 border-b border-border">
          <div className="p-3 bg-muted/50 border-r border-border">
            <span className="text-sm font-medium text-muted-foreground">Heure</span>
          </div>
          {weekDays.map((day, index) => {
            const isToday = isSameDay(day, new Date());
            return (
              <div
                key={index}
                className={cn(
                  "p-3 text-center border-r border-border last:border-r-0",
                  isToday ? "bg-[hsl(var(--renova-green))]/10" : "bg-muted/50"
                )}
              >
                <p className="text-sm font-medium text-muted-foreground">
                  {format(day, "EEE", { locale: fr })}
                </p>
                <p className={cn(
                  "text-lg font-bold",
                  isToday && "text-[hsl(var(--renova-green))]"
                )}>
                  {format(day, "d")}
                </p>
              </div>
            );
          })}
        </div>

        {/* Time Slots */}
        {HOURS.map((hour) => (
          <div key={hour} className="grid grid-cols-8 border-b border-border last:border-b-0">
            <div className="p-2 bg-muted/30 border-r border-border flex items-start justify-center">
              <span className="text-sm text-muted-foreground">{hour}</span>
            </div>
            {weekDays.map((day, dayIndex) => {
              const slotRdvs = getRdvsForSlot(day, hour);
              const isToday = isSameDay(day, new Date());
              // Afficher les paiements APF uniquement dans le créneau 08:00
              const dayPaiements = hour === "08:00" ? getPaiementsForDay(day) : [];
              
              return (
                <div
                  key={dayIndex}
                  className={cn(
                    "p-1 min-h-[48px] border-r border-border last:border-r-0",
                    isToday && "bg-[hsl(var(--renova-green))]/5"
                  )}
                >
                  {/* Paiements APF en premier (uniquement à 8h) */}
                  {dayPaiements.map((paiement) => (
                    <button
                      key={paiement.id}
                      onClick={() => handlePaiementClick(paiement)}
                      className="w-full p-2 rounded-lg text-left text-xs transition-all hover:scale-[1.02] mb-1 bg-amber-500/10 border border-amber-500/30"
                      data-testid={`paiement-${paiement.id}`}
                    >
                      <div className="flex items-center gap-1 mb-1">
                        <Euro className="w-3 h-3 text-amber-600" />
                        <span className="font-semibold text-amber-800 truncate">Paiement APF</span>
                      </div>
                      <p className="font-medium text-amber-700 truncate">
                        {formatCurrency(paiement.montant_ttc)}
                      </p>
                      <p className="text-amber-600 truncate text-[10px]">{paiement.numero_aaf}</p>
                    </button>
                  ))}
                  
                  {/* RDVs classiques */}
                  {slotRdvs.map((rdv) => {
                    const statusConfig = RDV_STATUS_CONFIG[rdv.statut] || RDV_STATUS_CONFIG.planifie;
                    const showOccupe = shouldShowOccupe(rdv);
                    return (
                      <button
                        key={rdv.id}
                        onClick={() => !showOccupe && handleRdvClick(rdv)}
                        className={cn(
                          "w-full p-2 rounded-lg text-left text-xs transition-all mb-1 border",
                          showOccupe
                            ? "bg-slate-100 border-slate-300 cursor-default"
                            : cn(statusConfig.bgLight, statusConfig.border, "hover:scale-[1.02]")
                        )}
                      >
                        {showOccupe ? (
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-slate-400"></div>
                            <span className="font-semibold text-slate-500">Occupé</span>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center gap-1 mb-1">
                              <div className={cn("w-2 h-2 rounded-full", (RDV_TYPE_COLORS[rdv.rdv_type] || RDV_TYPE_COLORS.rdv_admin).dot)}></div>
                              <span className="font-semibold truncate">{rdv.heure}</span>
                            </div>
                            <p className="font-medium truncate">{rdv.client_nom} {rdv.client_prenom}</p>
                            {rdv.rdv_type && rdv.rdv_type !== "rdv_admin" && (
                              <p className="text-[10px] font-bold mt-0.5" style={{color: "inherit"}}>{(RDV_TYPE_COLORS[rdv.rdv_type] || {}).label || rdv.rdv_type}</p>
                            )}
                            <p className="text-muted-foreground truncate">{rdv.attribue_a}</p>
                          </>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* RDV Detail Dialog */}
      <Dialog open={showRdvDialog} onOpenChange={setShowRdvDialog}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Détail du RDV</DialogTitle>
            <DialogDescription>
              {selectedRdv && format(parseISO(selectedRdv.date), "EEEE d MMMM yyyy", { locale: fr })} à {selectedRdv?.heure}
            </DialogDescription>
          </DialogHeader>
          
          {selectedRdv && (
            <div className="py-4 space-y-4">
              {/* Status Badge */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Statut :</span>
                <span className={cn(
                  "inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border",
                  RDV_STATUS_CONFIG[selectedRdv.statut]?.bgLight,
                  RDV_STATUS_CONFIG[selectedRdv.statut]?.border,
                  RDV_STATUS_CONFIG[selectedRdv.statut]?.textColor
                )}>
                  <div className={cn("w-2 h-2 rounded-full", RDV_STATUS_CONFIG[selectedRdv.statut]?.color)}></div>
                  {RDV_STATUS_CONFIG[selectedRdv.statut]?.label}
                </span>
              </div>

              {/* Client Info */}
              <div className="p-4 rounded-lg bg-muted/50 space-y-3">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium">{selectedRdv.client_nom} {selectedRdv.client_prenom}</span>
                </div>
                {selectedRdv.client_telephone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <a href={`tel:${selectedRdv.client_telephone}`} className="text-primary hover:underline">
                      {selectedRdv.client_telephone}
                    </a>
                  </div>
                )}
                {selectedRdv.client_adresse && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">{selectedRdv.client_adresse}</span>
                  </div>
                )}
                {selectedRdv.programme && (
                  <div className="flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">Programme : {selectedRdv.programme}</span>
                  </div>
                )}
              </div>

              {/* Assigned Person */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Assigné à :</span>
                <span className="font-medium">{selectedRdv.attribue_a}</span>
              </div>

              {/* Notes */}
              {selectedRdv.notes && (
                <div className="p-3 rounded-lg border bg-amber-500/10 border-amber-500/30">
                  <p className="text-sm text-amber-800">{selectedRdv.notes}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-4 border-t">
                <span className="text-sm text-muted-foreground w-full mb-2">Changer le statut :</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleUpdateRdvStatus("planifie")}
                  disabled={updating || selectedRdv.statut === "planifie"}
                  className="rounded-lg"
                >
                  <Clock className="w-4 h-4 mr-2 text-blue-600" />
                  Planifié
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleUpdateRdvStatus("realise")}
                  disabled={updating || selectedRdv.statut === "realise"}
                  className="rounded-lg"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-600" />
                  Réalisé
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleUpdateRdvStatus("annule")}
                  disabled={updating || selectedRdv.statut === "annule"}
                  className="rounded-lg"
                >
                  <XCircle className="w-4 h-4 mr-2 text-red-600" />
                  Annulé
                </Button>
              </div>

              {/* View Lead Button */}
              {selectedRdv.lead_id && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowRdvDialog(false);
                    navigate(`/leads/${selectedRdv.lead_id}`);
                  }}
                  className="w-full rounded-lg"
                >
                  Voir le lead associé
                </Button>
              )}
              {/* View Dossier Button - for any RDV with dossier_id */}
              {selectedRdv.dossier_id && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowRdvDialog(false);
                    navigate(`/dossiers/${selectedRdv.dossier_id}`);
                  }}
                  className="w-full rounded-lg border-emerald-500/40 text-emerald-700 hover:bg-emerald-500/10"
                  data-testid="btn-voir-dossier"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Voir le dossier associé
                </Button>
              )}
              {/* View Étude/Dossier for RDV Admin from Régie (legacy: etude_id) */}
              {selectedRdv.rdv_type === "rdv_admin" && selectedRdv.etude_id && !selectedRdv.dossier_id && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowRdvDialog(false);
                    navigate(`/etude/${selectedRdv.etude_id}`);
                  }}
                  className="w-full rounded-lg border-orange-300 text-orange-700 hover:bg-orange-50"
                  data-testid="btn-voir-etude-regie"
                >
                  Voir l'étude associée
                </Button>
              )}
              {selectedRdv.rdv_type === "rdv_admin" && !selectedRdv.etude_id && !selectedRdv.dossier_id && (
                <div className="p-3 rounded-lg bg-orange-50 border border-orange-200 text-xs text-orange-700">
                  RDV créé par <strong>{selectedRdv.created_by}</strong>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="destructive"
              onClick={handleDeleteRdv}
              disabled={updating}
              className="rounded-lg"
            >
              {updating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
              Supprimer le RDV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Paiement APF Detail Dialog */}
      <Dialog open={showPaiementDialog} onOpenChange={setShowPaiementDialog}>
        <DialogContent className="rounded-xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Euro className="w-5 h-5 text-amber-600" />
              Paiement APF prévu
            </DialogTitle>
            <DialogDescription>
              {selectedPaiement && `Échéance : ${selectedPaiement.date_display}`}
            </DialogDescription>
          </DialogHeader>
          
          {selectedPaiement && (
            <div className="py-4 space-y-4">
              {/* Numéro AAF */}
              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-amber-700" />
                  <span className="font-semibold text-amber-800">Référence APF</span>
                </div>
                <p className="font-mono text-lg text-amber-900">{selectedPaiement.numero_aaf || "N/A"}</p>
              </div>

              {/* Montants */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-lg bg-muted/50 text-center">
                  <p className="text-xs text-muted-foreground mb-1">Montant HT</p>
                  <p className="font-semibold">{formatCurrency(selectedPaiement.montant_ht)}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50 text-center">
                  <p className="text-xs text-muted-foreground mb-1">TVA</p>
                  <p className="font-semibold">{formatCurrency(selectedPaiement.montant_tva)}</p>
                </div>
                <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-center">
                  <p className="text-xs text-emerald-700 mb-1">Total TTC</p>
                  <p className="font-bold text-emerald-800">{formatCurrency(selectedPaiement.montant_ttc)}</p>
                </div>
              </div>

              {/* Dossier associé */}
              <div className="p-4 rounded-lg bg-muted/50 space-y-3">
                <div className="flex items-center gap-2">
                  <Building className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Dossier associé :</span>
                  <span className="font-medium">{selectedPaiement.dossier_nom}</span>
                </div>
                
                {selectedPaiement.date_document && (
                  <div className="flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Date du document :</span>
                    <span className="text-sm">{selectedPaiement.date_document}</span>
                  </div>
                )}
              </div>

              {/* Numéros de dossier APF */}
              {selectedPaiement.numeros_dossiers?.length > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">N° de dossiers inclus :</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedPaiement.numeros_dossiers.map((num, i) => (
                      <span key={i} className="px-2 py-1 bg-blue-500/10 text-blue-700 text-xs font-mono rounded border border-blue-500/30">
                        {num}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Bénéficiaires */}
              {selectedPaiement.beneficiaires?.length > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Bénéficiaires :</p>
                  <div className="space-y-1">
                    {selectedPaiement.beneficiaires.map((benef, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <User className="w-3 h-3 text-muted-foreground" />
                        {benef}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {selectedPaiement?.dossier_id && (
              <Button
                variant="outline"
                onClick={() => {
                  setShowPaiementDialog(false);
                  navigate(`/dossiers/${selectedPaiement.dossier_id}`);
                }}
                className="w-full sm:w-auto rounded-lg"
              >
                <Building className="w-4 h-4 mr-2" />
                Voir le dossier
              </Button>
            )}
            <Button
              variant="default"
              onClick={() => setShowPaiementDialog(false)}
              className="w-full sm:w-auto rounded-lg bg-[hsl(var(--renova-green))] hover:bg-[hsl(var(--renova-green))]/90"
            >
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New RDV Modal */}
      <Dialog open={showNewRdvModal} onOpenChange={setShowNewRdvModal}>
        <DialogContent className="rounded-xl max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><CalendarPlus className="w-5 h-5 text-blue-500" />Créer un RDV</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold">Date *</label>
                <Input type="date" value={newRdvSlot?.date || ""} onChange={e => setNewRdvSlot(p => ({...p, date: e.target.value}))} className="rounded-lg mt-1" min={new Date().toISOString().split("T")[0]} />
              </div>
              <div>
                <label className="text-xs font-semibold">Heure *</label>
                <select value={newRdvSlot?.heure || ""} onChange={e => setNewRdvSlot(p => ({...p, heure: e.target.value}))} className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm">
                  {Array.from({ length: 12 }, (_, i) => `${String(i + 9).padStart(2, "0")}:00`).map(h => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold">Nom *</label>
                <Input value={newRdvForm.client_nom} onChange={e => setNewRdvForm(p => ({...p, client_nom: e.target.value}))} className="rounded-lg mt-1" placeholder="Nom" />
              </div>
              <div>
                <label className="text-xs font-semibold">Prénom</label>
                <Input value={newRdvForm.client_prenom || ""} onChange={e => setNewRdvForm(p => ({...p, client_prenom: e.target.value}))} className="rounded-lg mt-1" placeholder="Prénom" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold">Téléphone</label>
              <Input value={newRdvForm.client_telephone} onChange={e => setNewRdvForm(p => ({...p, client_telephone: e.target.value}))} className="rounded-lg mt-1" placeholder="06..." />
            </div>
            <div>
              <label className="text-xs font-semibold">Objet du RDV</label>
              <Input value={newRdvForm.notes} onChange={e => setNewRdvForm(p => ({...p, notes: e.target.value}))} className="rounded-lg mt-1" placeholder="Objet..." />
            </div>
            <div>
              <label className="text-xs font-semibold">Relier à un dossier (optionnel)</label>
              <Input placeholder="Rechercher un dossier..." value={newRdvForm.dossierSearch || ""} onChange={e => setNewRdvForm(p => ({...p, dossierSearch: e.target.value}))} className="rounded-lg mt-1" />
              {newRdvForm.dossierSearch && (
                <div className="border border-border rounded-lg mt-1 max-h-[120px] overflow-y-auto divide-y divide-border">
                  {dossiersList.filter(d => (d.client_name || "").toLowerCase().includes((newRdvForm.dossierSearch || "").toLowerCase())).slice(0, 8).map(d => (
                    <button key={d.id} onClick={() => setNewRdvForm(p => ({...p, dossier_id: d.id, dossierSearch: "", client_nom: p.client_nom || d.client_name}))}
                      className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted/50">
                      <span className="font-semibold">{d.client_name}</span> <span className="text-muted-foreground">({d.programme})</span>
                    </button>
                  ))}
                </div>
              )}
              {newRdvForm.dossier_id && <p className="text-[10px] text-emerald-600 mt-1">Dossier lié : #{newRdvForm.dossier_id.slice(-6)}</p>}
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowNewRdvModal(false)} className="flex-1 rounded-lg">Annuler</Button>
              <Button onClick={handleCreateNewRdv} disabled={savingNewRdv || !newRdvForm.client_nom.trim() || !newRdvSlot?.date || !newRdvSlot?.heure} className="flex-1 rounded-lg bg-blue-500 hover:bg-blue-600 text-white">
                {savingNewRdv ? "Création..." : "Créer le RDV"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
